import { supabase } from "@/integrations/supabase/client";

export interface EnrichmentStats {
  totalBooks: number;
  needsEnrichment: number;
  hasSynopsis: number;
  hasSeries: number;
  hasPageCount: number;
  hasIsbn: number;
  inUserLibraries: number;
  enrichmentPending: number;
  enrichmentComplete: number;
  enrichmentFailed: number;
}

// Cache for stats with 5 minute expiry
let statsCache: { data: EnrichmentStats; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function getEnrichmentStats(useCache: boolean = true): Promise<EnrichmentStats> {
  try {
    // Check cache first
    if (useCache && statsCache && Date.now() - statsCache.timestamp < CACHE_DURATION) {
      console.log('[EnrichmentStats] Using cached data');
      return statsCache.data;
    }

    console.log('[EnrichmentStats] Fetching fresh data...');
    
    // Use optimized direct queries instead of slow RPC
    const [totalBooksResult, enrichmentStatusResult, userLibraryResult] = await Promise.all([
      supabase.from('books').select('id', { count: 'exact', head: true }),
      supabase.from('books').select('enrichment_status, synopsis, series_name, page_count, isbn13, isbn10', { count: 'exact', head: false }).limit(10000),
      supabase.from('user_books').select('book_id', { count: 'exact', head: true })
    ]);

    if (totalBooksResult.error) throw totalBooksResult.error;
    if (enrichmentStatusResult.error) throw enrichmentStatusResult.error;
    if (userLibraryResult.error) throw userLibraryResult.error;

    const totalBooks = totalBooksResult.count || 0;
    const inUserLibraries = userLibraryResult.count || 0;
    
    // Calculate from sample data (approximate but fast)
    const sampleData = enrichmentStatusResult.data || [];
    const sampleSize = sampleData.length;
    
    const stats = {
      totalBooks,
      needsEnrichment: sampleData.filter(b => !b.synopsis || !b.page_count).length * Math.floor(totalBooks / sampleSize),
      hasSynopsis: sampleData.filter(b => b.synopsis).length * Math.floor(totalBooks / sampleSize),
      hasSeries: sampleData.filter(b => b.series_name).length * Math.floor(totalBooks / sampleSize),
      hasPageCount: sampleData.filter(b => b.page_count).length * Math.floor(totalBooks / sampleSize),
      hasIsbn: sampleData.filter(b => b.isbn13 || b.isbn10).length * Math.floor(totalBooks / sampleSize),
      inUserLibraries,
      enrichmentPending: sampleData.filter(b => b.enrichment_status === 'pending').length * Math.floor(totalBooks / sampleSize),
      enrichmentComplete: sampleData.filter(b => b.enrichment_status === 'complete').length * Math.floor(totalBooks / sampleSize),
      enrichmentFailed: sampleData.filter(b => b.enrichment_status === 'failed').length * Math.floor(totalBooks / sampleSize)
    };

    // Cache the results
    statsCache = {
      data: stats,
      timestamp: Date.now()
    };

    return stats;
  } catch (error) {
    console.error('[EnrichmentStats] Failed to fetch:', error);
    
    // Return cached data if available, even if expired
    if (statsCache) {
      console.log('[EnrichmentStats] Returning stale cache due to error');
      return statsCache.data;
    }
    
    throw error;
  }
}

export function clearStatsCache() {
  statsCache = null;
}

export async function triggerBatchEnrichment() {
  try {
    console.log('[EnrichmentStats] Triggering batch enrichment...');
    
    const { data, error } = await supabase.functions.invoke('scheduled-batch-enrichment');

    if (error) {
      throw error;
    }

    console.log('[EnrichmentStats] Batch enrichment result:', data);
    return data;
  } catch (error) {
    console.error('[EnrichmentStats] Failed to trigger enrichment:', error);
    throw error;
  }
}
